/**
 * Right sidebar (owner 2026-09-28: "a watch list feature like TradingView has that can pop out on
 * the right hand side"): a slim icon bar on the right edge opens a collapsible, resizable panel
 * (TradingView's right toolbar / widget bar). Open/closed, width and tab are remembered **per
 * workspace**, inside the workspace entry of the synced `layout/desktop` document.
 */

export const SIDEBAR_TABS = ["watchlist", "details", "alerts", "objects", "notes"] as const;
export type SidebarTab = (typeof SIDEBAR_TABS)[number];
/** Tabs with content today; the others are placeholders (Object tree, Notes) shown disabled. */
export const SIDEBAR_READY: ReadonlySet<SidebarTab> = new Set(["watchlist", "details", "alerts"]);

export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 640;
export const SIDEBAR_DEFAULT_WIDTH = 300;

export type SidebarState = { open: boolean; width: number; tab: SidebarTab };

export const DEFAULT_SIDEBAR: SidebarState = { open: false, width: SIDEBAR_DEFAULT_WIDTH, tab: "watchlist" };

export function clampSidebarWidth(w: number): number {
  if (!Number.isFinite(w)) return SIDEBAR_DEFAULT_WIDTH;
  return Math.round(Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, w)));
}

/** A stored value → a valid state (anything missing takes the default). */
export function sanitizeSidebar(raw: unknown): SidebarState {
  if (typeof raw !== "object" || raw === null) return DEFAULT_SIDEBAR;
  const r = raw as Record<string, unknown>;
  const tab = (SIDEBAR_TABS as readonly unknown[]).includes(r["tab"])
    ? (r["tab"] as SidebarTab)
    : "watchlist";
  return {
    open: r["open"] === true,
    width: typeof r["width"] === "number" ? clampSidebarWidth(r["width"]) : SIDEBAR_DEFAULT_WIDTH,
    tab: SIDEBAR_READY.has(tab) ? tab : "watchlist",
  };
}

/**
 * Icon click (TradingView: clicking the open tab's icon closes the panel): another tab opens on
 * it; the current tab toggles open/closed.
 */
export function clickSidebarTab(s: SidebarState, tab: SidebarTab): SidebarState {
  if (!SIDEBAR_READY.has(tab)) return s;
  if (s.open && s.tab === tab) return { ...s, open: false };
  return { ...s, open: true, tab };
}
